export type ModelPortfolioType = 'GOAL' | 'SUB_ACCOUNT';

export type SubTradeRequestState =
  | 'INITIATED'
  | 'READY'
  | 'REQUESTED'
  | 'RECONCILED'
  | 'CANCELED';

export type SubTradeRequestType =
  | 'BUY'
  | 'SELL'
  | 'BUY_PENDING_CONTRIBUTION'
  | 'SELL_PENDING_WITHDRAW';

export type BulkTradeRunState =
  | 'GENERATING'
  | 'BULK_TRADES_INITIATED'
  | 'BULK_TRADES_FILES_GENERATING'
  | 'BULK_TRADES_REQUESTED'
  | 'BULK_TRADES_COMPLETED'
  | 'ALLOCATIONS_INITIATED'
  | 'ALLOCATIONS_REQUESTED'
  | 'ALLOCATIONS_COMPLETED'
  | 'COMPLETED'
  | 'COMPLETED_PENDING_PTFS'
  | 'CANCELED';

export type OptimizerState =
  | 'INITIATED'
  | 'OPTIMAL_PORTFOLIOS_GENERATING'
  | 'OPTIMAL_PORTFOLIOS_GENERATED'
  | 'SOLVING_FOR_REDEMPTIONS'
  | 'REDEMPTIONS_SOLVED'
  | 'SOLVING_FOR_PURCHASES'
  | 'PURCHASES_SOLVED'
  | 'REBALANCING'
  | 'REBALANCED'
  | 'TAX_OPTIMIZING'
  | 'TAX_OPTIMIZED'
  | 'GENERATING_ALLOCATIONS'
  | 'COMPLETED'
  | 'CANCELED'
  | 'FAILED';

export type PortfolioReportState = 'ACTIVE' | 'DRAFT' | 'INACTIVE';

export type Currency = 'USD' | 'CAD';

export type TradeSide = 'BUY' | 'SELL';

export type TranslatedName = {
  en: string;
  fr?: string;
};

export type UserBasic = {
  id: string;
  firstName: string;
  lastName?: string;
  entityName?: string;
};

export type HouseholdClientGroup = {
  id: string;
  name: string;
};

export type FinancialProductAssetClass = {
  key: string;
  translatedName?: TranslatedName;
};

export type FinancialProduct = {
  id: string;
  ticker: string;
  name?: string;
  translatedName?: TranslatedName;
  isCash: boolean;
  currentPriceCents: number;
  currency: Currency;
  taxRank?: number;
  exchange?: string;
  settlementDays?: number;
  primaryAssetClass?: FinancialProductAssetClass;
  secondaryAssetClass?: FinancialProductAssetClass;
};

export type FxRate = {
  from: Currency;
  to: Currency;
  rate: number;
};

export type GoalObject = {
  __typename: 'Goal';
  id: string;
  name: string;
  type?: string;
  householdClientGroup?: HouseholdClientGroup;
  statistics?: { marketValueCents: number };
  user?: UserBasic;
};

export type SubAccountObject = {
  __typename: 'SubAccount';
  id: string;
  account: {
    id: string;
    type: string;
    taxRank?: number;
    user: UserBasic;
  };
  goal?: {
    id: string;
    name: string;
    householdClientGroup?: HouseholdClientGroup;
  };
  statistics?: { marketValueCents: number };
};

export type PortfolioObject = GoalObject | SubAccountObject;

export type Holding = {
  currency: Currency;
  expectedValueCents: number;
  expectedPercentage: number;
  valueCents: number;
  quantity: number;
  pendingQuantity: number;
  taxRank?: number;
  isCashEquivalent: boolean;
  financialProduct: FinancialProduct;
};

export type SubAccountReport = {
  subAccount: {
    id: string;
    state: string;
    account: {
      taxRank?: number;
      type: string;
      user: { firstName: string; entityName?: string };
    };
  };
  currency: Currency;
  accountBaseCurrency?: Currency;
  cashCents: number;
  expectedCashCents: number;
  cashAvailableForTradeCents: number;
  marketValueCents: number;
  pendingWithdrawCents: number;
  pendingContributionCents?: number;
  pendingCashCents?: number;
  cashOnHoldToTradeCents?: number;
  holdings?: Holding[];
};

export type ModelPortfolio = {
  id: string;
  translatedName?: TranslatedName;
};

export type PortfolioReport = {
  id: string;
  modelPortfolioType: ModelPortfolioType;
  baseCurrency: Currency;
  originalCurrencies?: Currency[];
  object: PortfolioObject;
  modelPortfolio?: ModelPortfolio;
  cashCents: number;
  pendingCashCents: number;
  expectedCashCents: number;
  cashAvailableForTradeCents: number;
  expectedCashPercentage: number;
  marketValueCents: number;
  pendingSubTradeRequests: number;
  unverifiedSubTradeRequests: number;
  pendingSubTradeRequestAllocations: number;
  driftRebalanceRequired: boolean;
  transferRebalanceRequired: boolean;
  liquidateRebalanceRequired: boolean;
  state: PortfolioReportState;
  updatedAt: string;
  lastOptimizedBy?: {
    id: string;
    createdAt: string;
  };
  holdings: Holding[];
  subAccounts: SubAccountReport[];
  fxRates?: FxRate[];
};

export type DashboardPortfolioReport = {
  id: string;
  modelPortfolioType: 'GOAL' | 'SUB_ACCOUNT';
  driftRebalanceRequired: boolean;
  liquidateRebalanceRequired: boolean;
  transferRebalanceRequired: boolean;
  pendingSubTradeRequests: number;
  unverifiedSubTradeRequests: number;
  marketValueCents: number;
  cashCents?: number;
  expectedCashCents?: number;
  object?: {
    id?: string;
    name?: string;
    user?: { firstName?: string; lastName?: string };
    householdClientGroup?: { name?: string };
    account?: {
      type?: string;
      user?: { firstName?: string; lastName?: string };
    };
    goal?: {
      name?: string;
      householdClientGroup?: { name?: string };
    };
  };
};

export type DashboardOptimizer = {
  id: string;
  state: OptimizerState;
  numPortfolios: number;
  createdAt: string;
};

export type DashboardBulkTradeRun = {
  id: string;
  state: BulkTradeRunState;
  createdAt: string;
  allTradeRequestsByExchange?: { total: number }[];
};

export type SubTradeRequest = {
  id: string;
  state: SubTradeRequestState;
  type: SubTradeRequestType;
  isSellAll: boolean;
  moneyAllocatedCents: number;
  currency: Currency;
  settlementDate?: string;
  financialProduct: Pick<
    FinancialProduct,
    | 'id'
    | 'ticker'
    | 'currentPriceCents'
    | 'settlementDays'
    | 'currency'
    | 'translatedName'
  >;
  subAccount: {
    id: string;
    account: {
      id: string;
      type: string;
      user: Pick<UserBasic, 'id' | 'firstName'>;
    };
  };
  portfolioReport: string;
  preSplitMoneyAllocatedCents?: number;
  isProcessedForPair: boolean;
  pair?: {
    id: string;
    financialProduct: { ticker: string };
  };
  bulkTradeRun?: { id: string };
  verifiedAt?: string;
  verifiedByOrganizationUser?: {
    firstName: string;
    lastName: string;
  };
  blockedUntil?: string;
  blockedUntilAccountActive?: boolean;
  blockedUntilSubAccountActive?: boolean;
};

export type TransactionType = 'BUY' | 'SELL' | 'CANCEL_BUY' | 'CANCEL_SELL';

export type TradeTransaction = {
  id: string;
  type: TransactionType;
  quantity: number;
  priceCents: number;
  valueCents: number;
  currency: Currency;
  date: string;
  settleDate?: string;
  pending: boolean;
  description?: string;
  objectType: string;
  objectId?: string;
  subAccount?: {
    id: string;
    account: {
      id: string;
      type: string;
      user: { id: string; firstName: string; lastName?: string };
    };
  };
  financialProduct?: {
    id: string;
    ticker: string;
    translatedName?: TranslatedName;
  };
};

export type DisplayDensity = 'comfortable' | 'compact' | 'ultra-compact';

export type TabId = 'today' | 'portfolios' | 'operations' | 'tradeRequests';

export type StatusBarStats = {
  needsAction: number;
  inProgress: number;
  onTrack: number;
  totalAum: number;
  activeOperations: number;
  tradesInFlight: number;
};

export type FilterState = {
  // Trade request filters
  tradeState: SubTradeRequestState | 'ALL';
  tradeSide: TradeSide | 'ALL';
  tradeSearch: string;
  showReconciled: boolean;

  // Operations filters
  operationType: 'ALL' | 'optimizer' | 'bulkTradeRun';
  operationTimeRange: 'today' | 'yesterday' | 'last7days' | 'all';
  showCompleted: boolean;

  // Portfolio filters
  portfolioStatus: 'ALL' | 'urgent' | 'moderate' | 'onTrack';
  portfolioType: 'ALL' | 'GOAL' | 'SUB_ACCOUNT';
  portfolioSearch: string;
};

export const DEFAULT_FILTER_STATE: FilterState = {
  tradeState: 'ALL',
  tradeSide: 'ALL',
  tradeSearch: '',
  showReconciled: false,
  operationType: 'ALL',
  operationTimeRange: 'today',
  showCompleted: false,
  portfolioStatus: 'ALL',
  portfolioType: 'ALL',
  portfolioSearch: '',
};

export type TradeEntry = {
  holdingIndex: number;
  ticker: string;
  tradeAmountCents: number;
  currency: Currency;
  rawInput: string;
  side: 'BUY' | 'SELL';
  subAccountId: string;
};

export type AssetGroup = 'CASH' | 'EQUITIES' | 'FIXED_INCOME' | 'OTHER';

export type PortfolioStatus = {
  label: string;
  severity: 'error' | 'warning' | 'info' | 'success';
};

export type ValidationSeverity = 'pass' | 'warning' | 'error';

export type PreFlightCheck = {
  name: string;
  severity: ValidationSeverity;
  message: string;
};

export type PreFlightResult = {
  portfolioId: string;
  checks: PreFlightCheck[];
  overallSeverity: ValidationSeverity;
};

export type AggregateExposure = {
  ticker: string;
  productName: string;
  financialProductId: string;
  currency: Currency;
  totalValueCents: number;
  totalQuantity: number;
  portfolioCount: number;
  percentOfTotalAum: number;
  primaryAssetClassKey?: string;
  primaryAssetClassName?: string;
  portfolios: Array<{
    portfolioId: string;
    portfolioReportId: string;
    portfolioName: string;
    subAccountId: string;
    modelPortfolioType: string;
    valueCents: number;
    quantity: number;
    percentOfPortfolio: number;
    expectedPercentage: number;
    expectedValueCents: number;
  }>;
};

export type ModelAggregation = {
  modelId: string;
  modelName: string;
  portfolioCount: number;
  totalMarketValueCents: number;
  totalCashCents: number;
  avgDriftPct: number;
  needsRebalanceCount: number;
  pendingTradesCount: number;
  currencies: string[];
  portfolios: PortfolioReport[];
};

export type PipelineStage = {
  label: string;
  states: string[];
  active: boolean;
  completed: boolean;
};
